import { useEffect, useState } from 'react'
import api from '../services/api'
import {
  REMINDER_PRESETS, EVERYONE_LABEL, minutesLabel, emptyReminderRow,
  normalizeReminderRows, isIncompleteRow,
} from '../utils/reminders'
import { firmTz } from '../utils/eventTime'

// Feedback #828 (Hestia) — "select which member should receive the reminder,
// as well as the date, time, and week."
//
// Shared by the Calendar event form and the MatterDetail event form, so the
// two can never offer different reminder options again (MatterDetail had no
// reminder control at all before this).
//
// Each row is WHEN + WHO:
//   when — a preset offset (15 min … 2 weeks before) OR "Specific date & time",
//          typed on the FIRM's clock (converted to UTC on submit by
//          reminderRowsToPayload — never here).
//   who  — "Everyone who can see this event" (default; the only behaviour
//          before #828) or one team member.  The API refuses a person who
//          cannot see the event, so picking one never widens visibility.
//
// Props:
//   value        the form's reminders array (editor rows; legacy preset
//                strings from old localStorage drafts are accepted)
//   onChange     (rows) => void
//   teamMembers  optional; fetched from /api/team when omitted
//   idPrefix     optional, for unique input ids when several forms mount

// Service accounts (monitor@internal etc.) are not people to remind.
const isServiceAccount = (email) => /@(internal|service\.local)$/i.test(email || '')

export default function ReminderEditor ({ value, onChange, teamMembers, idPrefix = 'rem' }) {
  const [fetchedTeam, setFetchedTeam] = useState([])
  const needFetch = !Array.isArray(teamMembers)

  useEffect(() => {
    if (!needFetch) return
    let cancelled = false
    api.get('/api/team')
      .then(res => { if (!cancelled) setFetchedTeam(Array.isArray(res.data) ? res.data : []) })
      .catch(() => { /* picker falls back to "Everyone" only */ })
    return () => { cancelled = true }
  }, [needFetch])

  const team = (needFetch ? fetchedTeam : teamMembers)
    .filter(m => m && m.is_active !== false && !isServiceAccount(m.email))
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))

  const tz = firmTz()
  const rows = normalizeReminderRows(value, tz)

  const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i))
  const addRow = () => {
    const used = new Set(rows.filter(r => r.mode === 'offset').map(r => Number(r.minutes)))
    const next = REMINDER_PRESETS.find(p => !used.has(p.minutes))
    onChange([...rows, emptyReminderRow(next ? next.minutes : 60)])
  }

  return (
    <div className="space-y-2">
      {rows.map((row, i) => {
        const custom = row.mode === 'offset' && !REMINDER_PRESETS.some(p => String(p.minutes) === String(row.minutes))
        const whenValue = row.mode === 'at' ? 'at' : String(row.minutes)
        // Keep a recipient who is no longer in the list (e.g. deactivated) as
        // an explicit option, so the controlled select does not silently read
        // as "Everyone" — the API will reject them with a clear message.
        const recipientMissing = row.recipient_user_id &&
          !team.some(m => String(m.id) === String(row.recipient_user_id))
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-2 space-y-2 bg-white">
            <div className="flex gap-2 items-center">
              <label htmlFor={`${idPrefix}-when-${i}`} className="sr-only">When</label>
              <select id={`${idPrefix}-when-${i}`} value={whenValue}
                onChange={e => {
                  const v = e.target.value
                  if (v === 'at') setRow(i, { mode: 'at' })
                  else setRow(i, { mode: 'offset', minutes: v })
                }}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                {REMINDER_PRESETS.map(p => (
                  <option key={p.minutes} value={String(p.minutes)}>{p.label}</option>
                ))}
                {custom && <option value={String(row.minutes)}>{minutesLabel(row.minutes)}</option>}
                <option value="at">📅 Specific date &amp; time…</option>
              </select>
              <button type="button" onClick={() => removeRow(i)}
                aria-label="Remove reminder" title="Remove reminder"
                className="text-red-500 hover:text-red-700 px-2 text-lg">×</button>
            </div>

            {row.mode === 'at' && (
              <div className="flex gap-2 items-center flex-wrap">
                <label htmlFor={`${idPrefix}-date-${i}`} className="text-xs text-gray-600">Date</label>
                <input id={`${idPrefix}-date-${i}`} type="date" value={row.at_date}
                  onChange={e => setRow(i, { at_date: e.target.value })}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <label htmlFor={`${idPrefix}-time-${i}`} className="text-xs text-gray-600">Time</label>
                <input id={`${idPrefix}-time-${i}`} type="time" value={row.at_time}
                  onChange={e => setRow(i, { at_time: e.target.value })}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <span className="text-[11px] text-gray-500">firm time; no time = 9:00 AM</span>
                {isIncompleteRow(row) && (
                  <p className="w-full text-[11px] text-amber-700">Pick a date — this reminder won't be saved without one.</p>
                )}
              </div>
            )}

            <div className="flex gap-2 items-center">
              <label htmlFor={`${idPrefix}-who-${i}`} className="text-xs text-gray-600 shrink-0">Remind</label>
              <select id={`${idPrefix}-who-${i}`} value={row.recipient_user_id || ''}
                onChange={e => setRow(i, { recipient_user_id: e.target.value })}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">{EVERYONE_LABEL}</option>
                {recipientMissing && (
                  <option value={row.recipient_user_id}>Team member #{row.recipient_user_id}</option>
                )}
                {team.map(m => (
                  <option key={m.id} value={String(m.id)}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
        )
      })}
      <button type="button" onClick={addRow}
        className="text-xs text-primary-600 hover:underline">
        + Add reminder
      </button>
    </div>
  )
}
